import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';

const API_URL = "https://holisticphysiotherapy.in/holistic_bill/api.php"; 

export default function Reports() {
  const todayStr = new Date().toISOString().split('T')[0];
  
  const [fromDate, setFromDate] = useState(todayStr);
  const [toDate, setToDate] = useState(todayStr);
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch Report Data
  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}?action=get_billing_report&from_date=${fromDate}&to_date=${toDate}`);
      if (res.data.status === 'success') {
        setReportData(res.data.data || []);
      }
    } catch (err) {
      alert("Failed to fetch report data.");
    } finally {
      setLoading(false);
    }
  };

  // Automatically fetch today's report on mount
  useEffect(() => {
    fetchReport();
  }, []);

  // Set filter to Today
  const setTodayFilter = () => {
    setFromDate(todayStr);
    setToDate(todayStr);
  };

  // Export to Excel
  const exportToExcel = () => {
    if (reportData.length === 0) {
      alert("No data available to export.");
      return;
    }

    // Format data cleanly for spreadsheet rows
    const excelRows = reportData.map((row, idx) => ({
      'S.No': idx + 1,
      'Bill No': row.bill_no,
      'Date & Time': new Date(row.bill_date).toLocaleString(),
      'UHID': row.uhid,
      'Patient Name': row.patient_name || 'N/A',
      'Mobile': row.phone || 'N/A',
      'Subtotal (₹)': parseFloat(row.subtotal),
      'Discount (₹)': parseFloat(row.overall_discount),
      'Total Amount (₹)': parseFloat(row.total_amount),
      'Cash (₹)': parseFloat(row.cash_amount),
      'UPI (₹)': parseFloat(row.upi_amount),
      'Card (₹)': parseFloat(row.card_amount),
      'Payment Mode': row.payment_mode,
      'Billed By': row.created_by || 'Admin'
    }));

    // Create Worksheet and Workbook
    const worksheet = XLSX.utils.json_to_sheet(excelRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Billing_Report");

    // Save File
    XLSX.writeFile(workbook, `Billing_Report_${fromDate}_to_${toDate}.xlsx`);
  };

  // Summary Totals Calculation
  const totalRevenue = reportData.reduce((acc, r) => acc + parseFloat(r.total_amount || 0), 0);
  const totalCash = reportData.reduce((acc, r) => acc + parseFloat(r.cash_amount || 0), 0);
  const totalUpi = reportData.reduce((acc, r) => acc + parseFloat(r.upi_amount || 0), 0);
  const totalCard = reportData.reduce((acc, r) => acc + parseFloat(r.card_amount || 0), 0);

  return (
    <div className="container-fluid px-4 py-3">
      <div className="card border-0 shadow-sm p-3 mb-4 bg-white rounded">
        <h4 className="fw-bold mb-3"   style={{color:"#007C9D"}}>📊 Billing Reports & Analytics</h4>
        
        {/* Filter Controls */}
        <div className="row g-3 align-items-end">
          <div className="col-md-3">
            <label className="form-label fw-bold small">From Date</label>
            <input 
              type="date" 
              className="form-control" 
              value={fromDate} 
              onChange={e => setFromDate(e.target.value)} 
            />
          </div>
          <div className="col-md-3">
            <label className="form-label fw-bold small">To Date</label>
            <input 
              type="date" 
              className="form-control" 
              value={toDate} 
              onChange={e => setToDate(e.target.value)} 
            />
          </div>
          <div className="col-md-6 d-flex gap-2">
            <button className="btn fw-bold"  style={{backgroundColor:"#007C9D", color:"white"}} onClick={fetchReport} disabled={loading}>
              {loading ? 'Loading...' : '🔍 Get Report'}
            </button>
            <button className="btn btn-outline-secondary fw-bold" onClick={() => { setTodayFilter(); fetchReport(); }}>
              📅 Today's Report
            </button>
            <button className="btn btn-success fw-bold ms-auto" onClick={exportToExcel} disabled={reportData.length === 0}>
              📥 Export to Excel (.xlsx)
            </button>
          </div>
        </div>
      </div>

      {/* Summary KPI Cards */}
      <div className="row g-3 mb-4">
        <div className="col-md-3">
          <div className="card border-0 shadow-sm text-white p-3 rounded" style={{backgroundColor:"#007C9D", color:"white"}}>
            <small className="text-uppercase fw-bold opacity-75">Total Revenue</small>
            <h3 className="fw-bold mb-0">₹{totalRevenue.toFixed(2)}</h3>
            <small>{reportData.length} Bills Generated</small>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm bg-success text-white p-3 rounded">
            <small className="text-uppercase fw-bold opacity-75">Cash Collected</small>
            <h3 className="fw-bold mb-0">₹{totalCash.toFixed(2)}</h3>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm bg-primary text-white p-3 rounded">
            <small className="text-uppercase fw-bold opacity-75">UPI Collected</small>
            <h3 className="fw-bold mb-0">₹{totalUpi.toFixed(2)}</h3>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm bg-dark text-white p-3 rounded">
            <small className="text-uppercase fw-bold opacity-75">Card Collected</small>
            <h3 className="fw-bold mb-0">₹{totalCard.toFixed(2)}</h3>
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="card border-0 shadow-sm p-3 bg-white rounded">
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>#</th>
                <th>Bill No</th>
                <th>Date & Time</th>
                <th>Patient Name</th>
                <th>UHID</th>
                <th className="text-end">Total Amount</th>
                <th>Payment Mode</th>
                <th>Billed By</th>
              </tr>
            </thead>
            <tbody>
              {reportData.map((row, idx) => (
                <tr key={row.id}>
                  <td>{idx + 1}</td>
                  <td className="fw-bold">{row.bill_no}</td>
                  <td>{new Date(row.bill_date).toLocaleString()}</td>
                  <td>{row.patient_name || 'N/A'}</td>
                  <td>{row.uhid}</td>
                  <td className="text-end fw-bold">₹{parseFloat(row.total_amount).toFixed(2)}</td>
                  <td>
                    <span className="badge bg-secondary">{row.payment_mode}</span>
                  </td>
                  <td>{row.created_by || 'Admin'}</td>
                </tr>
              ))}
              {reportData.length === 0 && (
                <tr>
                  <td colSpan="8" className="text-center py-4 text-muted">
                    No billing records found for the selected date range.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}